import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';

const useStyles = makeStyles((theme: any) => ({
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    fontSize: '0.85rem',
    marginBottom: theme.spacing(0.5),
  },
  detailsRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  detailsLabel: {
    fontWeight: 500,
    minWidth: '100px',
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  detailsValue: {
    flex: 1,
    fontFamily: 'inherit',
    fontSize: '0.8rem',
  },
  overrideFlag: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(0.5),
  },
  entityList: {
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.divider}`,
  },
  entityItem: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
}));

type SettlementPartyDetailsProps = {
  entryDate?: string;
  isPosted?: boolean;
  isOverride: boolean;
  calculatedAmount: number;
  overriddenAmount: number;
  partyRoles?: string[];
  entities?: string[];
  t: TFunction;
};

export const SettlementPartyDetails: FC<SettlementPartyDetailsProps> = ({
  entryDate,
  isPosted,
  isOverride,
  calculatedAmount,
  overriddenAmount,
  partyRoles = [],
  entities = [],
  t,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.detailsContainer}>
      {isOverride && calculatedAmount !== 0 && (
        <Box className={classes.detailsRow}>
          <Typography className={classes.detailsLabel}>
            <FontAwesomeIcon icon="exclamation-circle" className={classes.overrideFlag} />
            {t('settlement:override-flag', 'Override')}
            :
          </Typography>
          <Typography className={classes.detailsValue}>
            {calculatedAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' → '}
            {overriddenAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
      )}

      {entryDate && (
        <Box className={classes.detailsRow}>
          <Typography className={classes.detailsLabel}>
            {t('settlement:entry-date', 'Entry Date')}
            :
          </Typography>
          <Typography className={classes.detailsValue}>
            {isPosted ? '✓' : '○'}
            {' '}
            {new Date(entryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </Typography>
        </Box>
      )}

      {entities.length > 0 && (
        <Box className={classes.detailsRow}>
          <Typography className={classes.detailsLabel}>
            {t('settlement:entity', 'Entity')}
            :
          </Typography>
          <Box className={classes.entityList}>
            {Array.from(new Set(entities)).map((entity) => (
              <Typography key={entity} className={classes.entityItem}>
                {entity}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {partyRoles.length > 0 && (
        <Box className={classes.detailsRow}>
          <Typography className={classes.detailsLabel}>
            {t('settlement:party-role', 'Party Role')}
            :
          </Typography>
          <Typography className={classes.detailsValue}>
            {partyRoles.length > 1
              ? `${partyRoles.slice(0, -1).join(', ')}, and ${partyRoles[partyRoles.length - 1]}`
              : partyRoles[0]}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
